import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { supabase as publicSupabase } from "@/integrations/supabase/client";
import { maskOrderRef } from "@/lib/reviews/mask";
import { assertReviewsAdmin, getCustomerIdsForReviewLookup } from "@/lib/reviews.server";
import { ReviewImagesSchema, ReviewSortSchema } from "@/lib/reviews.schemas";

// ─────────── Public: list approved reviews for a product ───────────

export const getProductReviews = createServerFn({ method: "GET" })
  .inputValidator((d: { productId: string; sort?: string; rating?: number }) =>
    z
      .object({
        productId: z.string().uuid(),
        sort: ReviewSortSchema.optional(),
        rating: z.number().int().min(1).max(5).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const supabase = publicSupabase;
    let q = supabase
      .from("reviews")
      .select("id, rating, title, comment, images, reviewer_name, is_verified, reviewer_kind, created_at, order_id, admin_reply, admin_reply_at")
      .eq("product_id", data.productId)
      .eq("status", "approved");

    if (data.rating) q = q.eq("rating", data.rating);

    const sort = data.sort ?? "newest";
    if (sort === "highest") q = q.order("rating", { ascending: false }).order("created_at", { ascending: false });
    else if (sort === "lowest") q = q.order("rating", { ascending: true }).order("created_at", { ascending: false });
    else q = q.order("created_at", { ascending: false });

    const { data: rows, error } = await q.limit(50);
    if (error) {
      console.warn("Product reviews lookup failed", error.message);
      return {
        reviews: [],
        stats: { avg: 0, count: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as Record<number, number> },
        verifiedStats: { avg: 0, count: 0 },
      };
    }

    // Distribution + average
    const { data: agg } = await supabase
      .from("reviews")
      .select("rating, reviewer_kind")
      .eq("product_id", data.productId)
      .eq("status", "approved");

    const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as Record<number, number>;
    let sum = 0;
    // Structured-data rating stays purchase-backed: beta tester feedback is
    // shown on the page but excluded from the aggregate Google can surface.
    let verifiedSum = 0;
    let verifiedCount = 0;
    (agg || []).forEach((r: any) => {
      dist[r.rating] = (dist[r.rating] || 0) + 1;
      sum += r.rating;
      if ((r.reviewer_kind ?? "customer") === "customer") {
        verifiedSum += r.rating;
        verifiedCount += 1;
      }
    });
    const count = agg?.length ?? 0;
    const avg = count ? Number((sum / count).toFixed(2)) : 0;
    const verifiedAvg = verifiedCount ? Number((verifiedSum / verifiedCount).toFixed(2)) : 0;

    // Helpful counts (aggregate only — never exposes who voted)
    const helpful: Record<string, number> = {};
    try {
      const rpc = supabase.rpc.bind(supabase) as unknown as (
        n: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: Array<{ review_id: string; helpful_count: number }> | null }>;
      const { data: votes } = await rpc("review_helpful_counts", { _product_id: data.productId });
      (votes || []).forEach((v) => {
        helpful[v.review_id] = Number(v.helpful_count) || 0;
      });
    } catch {
      /* helpful counts are non-critical */
    }

    // Full order IDs for the "Verified purchase" badge.
    const orderRefs: Record<string, string> = {};
    try {
      const ids = Array.from(new Set((rows ?? []).map((r: any) => r.order_id).filter(Boolean)));
      if (ids.length) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: orders } = await supabaseAdmin.from("orders").select("id, name").in("id", ids);
        (orders || []).forEach((o: any) => {
          const masked = maskOrderRef(o.name);
          if (masked) orderRefs[o.id] = masked;
        });
      }
    } catch {
      /* order refs are non-critical */
    }

    let list = (rows ?? []).map((r: any) => {
      const { order_id, ...rest } = r;
      return {
        ...rest,
        helpful_count: helpful[r.id] ?? 0,
        order_ref: order_id ? (orderRefs[order_id] ?? null) : null,
      };
    });
    if (sort === "helpful") {
      list = list.sort(
        (a, b) =>
          b.helpful_count - a.helpful_count ||
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    }

    return {
      reviews: list,
      stats: { avg, count, distribution: dist },
      verifiedStats: { avg: verifiedAvg, count: verifiedCount },
    };
  });

// ─────────── Public: mark a review as helpful (one vote per person) ───────────

export const voteReviewHelpful = createServerFn({ method: "POST" })
  .inputValidator((d: { reviewId: string; visitorKey?: string }) =>
    z
      .object({
        reviewId: z.string().uuid(),
        visitorKey: z.string().trim().min(6).max(120).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Only approved reviews can be voted on.
    const { data: review } = await supabaseAdmin
      .from("reviews")
      .select("id, status")
      .eq("id", data.reviewId)
      .maybeSingle();
    if (!review || (review as any).status !== "approved") return { ok: false as const };

    const raw = data.visitorKey ?? "";
    if (!raw) return { ok: false as const };
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`rv:${raw}`));
    const voterKey = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    await supabaseAdmin
      .from("review_votes" as never)
      .insert({ review_id: data.reviewId, voter_key: voterKey } as never);

    const { count } = await supabaseAdmin
      .from("review_votes" as never)
      .select("id", { count: "exact", head: true })
      .eq("review_id", data.reviewId);

    return { ok: true as const, helpfulCount: count ?? 0 };
  });


export const getProductReviewStatsBatch = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => {
    const result = z
      .object({ productIds: z.array(z.string().uuid()).max(50).default([]) })
      .safeParse(input ?? {});
    return result.success ? result.data : { productIds: [] };
  })
  .handler(async ({ data }) => {
    if (!data.productIds.length) return { stats: {} as Record<string, { avg: number; count: number }> };
    const supabase = publicSupabase;
    const { data: rows, error } = await supabase
      .from("product_review_stats")
      .select("product_id, avg_rating, review_count")
      .in("product_id", data.productIds);
    if (error) {
      console.warn("Review stats lookup failed", error.message);
      return { stats: {} as Record<string, { avg: number; count: number }> };
    }
    const stats: Record<string, { avg: number; count: number }> = {};
    (rows || []).forEach((r: any) => {
      stats[r.product_id] = { avg: Number(r.avg_rating) || 0, count: Number(r.review_count) || 0 };
    });
    return { stats };
  });

// ─────────── Public: review stats keyed by product handle ───────────

export const getProductReviewStatsByHandle = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => {
    const result = z
      .object({ handles: z.array(z.string().min(1)).max(20).default([]) })
      .safeParse(input ?? {});
    return result.success ? result.data : { handles: [] };
  })
  .handler(async ({ data }) => {
    const empty = { stats: {} as Record<string, { avg: number; count: number }> };
    if (!data.handles.length) return empty;
    const supabase = publicSupabase;
    const { data: products, error: pErr } = await supabase
      .from("products")
      .select("id, handle")
      .in("handle", data.handles);
    if (pErr || !products?.length) return empty;
    const byId = new Map((products as any[]).map((p) => [p.id as string, p.handle as string]));
    const { data: rows, error } = await supabase
      .from("product_review_stats")
      .select("product_id, avg_rating, review_count")
      .in("product_id", Array.from(byId.keys()));
    if (error) return empty;
    const stats: Record<string, { avg: number; count: number }> = {};
    (rows || []).forEach((r: any) => {
      const handle = byId.get(r.product_id);
      if (!handle) return;
      stats[handle] = { avg: Number(r.avg_rating) || 0, count: Number(r.review_count) || 0 };
    });
    return { stats };
  });

// ─────────── Public: recent approved reviews across all products (homepage mix) ───────────

export const getRecentReviewsMix = createServerFn({ method: "GET" })
  .inputValidator((d?: { limit?: number }) =>
    z.object({ limit: z.number().int().min(1).max(24).optional() }).parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const limit = data.limit ?? 9;
    const supabase = publicSupabase;
    const { data: rows, error } = await supabase
      .from("reviews")
      .select("id, rating, title, comment, reviewer_name, is_verified, reviewer_kind, created_at, product_id, order_id, products(title, handle)")
      .eq("status", "approved")
      .gte("rating", 4)
      .order("created_at", { ascending: false })
      .limit(limit * 3);
    if (error) {
      console.warn("Recent reviews lookup failed", error.message);
      return { reviews: [] };
    }

    const perProduct: Record<string, number> = {};
    const picked: any[] = [];
    for (const r of rows ?? []) {
      const pid = (r as any).product_id as string;
      if ((perProduct[pid] ?? 0) >= 2) continue;
      perProduct[pid] = (perProduct[pid] ?? 0) + 1;
      picked.push(r);
      if (picked.length >= limit) break;
    }

    // Same masked order-ref treatment as the product page badge.
    const orderRefs: Record<string, string> = {};
    try {
      const ids = Array.from(new Set(picked.map((r: any) => r.order_id).filter(Boolean)));
      if (ids.length) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: orders } = await supabaseAdmin.from("orders").select("id, name").in("id", ids);
        (orders || []).forEach((o: any) => {
          const masked = maskOrderRef(o.name);
          if (masked) orderRefs[o.id] = masked;
        });
      }
    } catch {
      /* order refs are non-critical */
    }

    return {
      reviews: picked.map(({ order_id, ...rest }: any) => ({
        ...rest,
        order_ref: order_id ? (orderRefs[order_id] ?? null) : null,
      })),
    };
  });


// ─────────── Public: site-wide approved review aggregate ───────────

/**
 * Average and count across every approved review on the site.
 * Beta tester feedback is shown and counted on-page, but kept separate so
 * structured data can stay purchase-backed.
 */
export const getSiteReviewStats = createServerFn({ method: "GET" }).handler(async () => {
  const { data: rows, error } = await publicSupabase
    .from("reviews")
    .select("rating, reviewer_kind")
    .eq("status", "approved");

  if (error || !rows) {
    console.warn("Site review stats lookup failed", error?.message);
    return { avg: 0, count: 0, verifiedAvg: 0, verifiedCount: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as Record<number, number> };
  }

  const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as Record<number, number>;
  let sum = 0;
  let verifiedSum = 0;
  let verifiedCount = 0;
  rows.forEach((r: any) => {
    distribution[r.rating] = (distribution[r.rating] || 0) + 1;
    sum += r.rating;
    if ((r.reviewer_kind ?? "customer") === "customer") {
      verifiedSum += r.rating;
      verifiedCount += 1;
    }
  });
  const count = rows.length;
  return {
    avg: count ? Number((sum / count).toFixed(2)) : 0,
    count,
    verifiedAvg: verifiedCount ? Number((verifiedSum / verifiedCount).toFixed(2)) : 0,
    verifiedCount,
    distribution,
  };
});

// ─────────── Auth: reviewable products for current user ───────────

export const getReviewableProducts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = ((context.claims as any)?.email as string | undefined)?.toLowerCase();

    // Match orders by either linked customer or verified email
    const { data: orders, error } = await supabaseAdmin
      .from("orders")
      .select(
        "id, name, status, placed_at, customer_id, email, order_items(variant_id, variants(product_id, products(id, handle, title)))",
      )
      .or(`email.eq.${email || ""},customer_id.in.(${await getCustomerIdsForReviewLookup(supabaseAdmin, context.userId)})`)
      .in("status", ["delivered", "fulfilled", "completed"])
      .order("placed_at", { ascending: false })
      .limit(50);


    if (error) throw new Error(error.message);

    const { data: existing } = await supabaseAdmin
      .from("reviews")
      .select("product_id, status, rating, title, comment, created_at, updated_at")
      .eq("user_id", context.userId);
    const reviewedMap = new Map<string, any>();
    (existing || []).forEach((r: any) => reviewedMap.set(r.product_id, r));

    type Item = { orderId: string; orderName: string; placedAt: string; productId: string; handle: string; title: string };
    const items: Item[] = [];
    const seen = new Set<string>();
    (orders || []).forEach((o: any) => {
      (o.order_items || []).forEach((it: any) => {
        const v = Array.isArray(it.variants) ? it.variants[0] : it.variants;
        const p = v ? (Array.isArray(v.products) ? v.products[0] : v.products) : null;
        if (!p?.id) return;
        const key = `${o.id}:${p.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        items.push({
          orderId: o.id,
          orderName: o.name,
          placedAt: o.placed_at,
          productId: p.id,
          handle: p.handle,
          title: p.title,
        });
      });
    });

    return {
      items: items.map((i) => {
        const r = reviewedMap.get(i.productId);
        return {
          ...i,
          alreadyReviewed: Boolean(r),
          reviewStatus: (r?.status as "pending" | "approved" | "rejected" | undefined) ?? null,
          reviewSubmittedAt: r?.created_at ?? null,
        };
      }),
    };
  });

// ─────────── Auth: submit review ───────────

export const submitReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (d: {
      productId: string;
      orderId?: string;
      rating: number;
      title?: string;
      comment: string;
      images?: string[];
      reviewerName?: string;
    }) =>
      z
        .object({
          productId: z.string().uuid(),
          orderId: z.string().uuid().optional(),
          rating: z.number().int().min(1).max(5),
          title: z.string().trim().max(120).optional(),
          comment: z.string().trim().min(3).max(2000),
          images: ReviewImagesSchema.optional(),
          reviewerName: z.string().trim().max(80).optional(),
        })
        .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId, claims } = context;
    const displayName =
      data.reviewerName ||
      ((claims as any)?.user_metadata?.full_name as string | undefined) ||
      ((claims as any)?.email as string | undefined)?.split("@")[0] ||
      "Verified buyer";

    const { error } = await supabase.from("reviews").insert({
      user_id: userId,
      product_id: data.productId,
      order_id: data.orderId ?? null,
      rating: data.rating,
      title: data.title ?? null,
      comment: data.comment,
      images: data.images ?? [],
      reviewer_name: displayName,
      status: "pending",
      is_verified: true,
    });

    if (error) {
      if (error.code === "23505") return { ok: false, error: "You have already reviewed this product." };
      if ((error.message || "").toLowerCase().includes("row-level"))
        return { ok: false, error: "Only verified buyers can submit a review." };
      return { ok: false, error: error.message };
    }

    // For multi-product orders, only mark the order's review_request completed
    // once every product on that order has been reviewed by this user.
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      if (data.orderId) {
        // Collect distinct product IDs on this order.
        const { data: orderItems } = await supabaseAdmin
          .from("order_items")
          .select("variants(product_id)")
          .eq("order_id", data.orderId);
        const orderProductIds = new Set<string>();
        (orderItems || []).forEach((it: any) => {
          const v = Array.isArray(it.variants) ? it.variants[0] : it.variants;
          const pid = v?.product_id;
          if (pid) orderProductIds.add(pid);
        });

        // Reviews this user has already submitted for products on this order.
        const { data: reviewed } = await supabaseAdmin
          .from("reviews")
          .select("product_id")
          .eq("user_id", userId)
          .in("product_id", Array.from(orderProductIds));
        const reviewedIds = new Set((reviewed || []).map((r: any) => r.product_id));

        const allReviewed =
          orderProductIds.size > 0 && Array.from(orderProductIds).every((pid) => reviewedIds.has(pid));

        if (allReviewed) {
          await supabaseAdmin
            .from("review_requests")
            .update({ reviewed_at: new Date().toISOString(), status: "completed" })
            .eq("order_id", data.orderId);
        } else {
          // Record partial progress without closing the request.
          await supabaseAdmin
            .from("review_requests")
            .update({ reviewed_at: new Date().toISOString() })
            .eq("order_id", data.orderId)
            .is("reviewed_at", null);
        }
      }
    } catch (_) {}

    return { ok: true };
  });

// ─────────── Admin moderation ───────────

export const adminListReviews = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { status?: string; id?: string } | undefined) =>
    z
      .object({ id: z.string().uuid().optional(), status: z.enum(["pending", "approved", "rejected", "all"]).optional() })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertReviewsAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("reviews")
      .select(
        "id, rating, title, comment, images, reviewer_name, status, is_verified, created_at, user_id, order_id, product_id, admin_reply, admin_reply_at, products(handle, title)",
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (data.id) q = q.eq("id", data.id);
    const status = data.status ?? "pending";
    if (status !== "all") q = q.eq("status", status);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { reviews: rows ?? [] };
  });

export const adminModerateReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; status: "approved" | "rejected" }) =>
    z.object({ id: z.string().uuid(), status: z.enum(["approved", "rejected"]) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertReviewsAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: before } = await supabaseAdmin
      .from("reviews")
      .select("status")
      .eq("id", data.id)
      .maybeSingle();
    const { error } = await supabaseAdmin
      .from("reviews")
      .update({ status: data.status })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    const { logAdminAction } = await import("@/lib/audit.server");
    await logAdminAction({
      context,
      action: `review_${data.status}`,
      entityType: "review",
      entityId: data.id,
      before: before ?? undefined,
      after: { status: data.status },
    });

    // ── TruCoins reward on approval (idempotent), reversal on rejection ──
    let reward: { coins: number; awarded: boolean } = { coins: 0, awarded: false };
    try {
      if (data.status === "approved") {
        const { data: res, error: rpcErr } = await supabaseAdmin.rpc(
          "loyalty_award_review_bonus" as never,
          { _review_id: data.id } as never,
        );
        const row: any = Array.isArray(res) ? res[0] : res;
        if (!rpcErr && row?.awarded) {
          reward = { coins: Number(row.coins || 0), awarded: true };
          // Notify the customer over WhatsApp using the approved template.
          const { data: cust } = await supabaseAdmin
            .from("customers")
            .select("id, phone, name")
            .eq("id", row.customer_id)
            .maybeSingle();
          const { data: review } = await supabaseAdmin
            .from("reviews")
            .select("order_id, orders(name)")
            .eq("id", data.id)
            .maybeSingle();
          if (cust?.phone) {
            const { POINT_VALUE_PAISE } = await import("@/lib/loyalty/coins");
            const { sendTruCoinsCredited } = await import("@/lib/whatsapp/templates.server");
            const orderRef =
              (Array.isArray((review as any)?.orders)
                ? (review as any)?.orders?.[0]?.name
                : (review as any)?.orders?.name) || "your review";
            await sendTruCoinsCredited(cust.phone, {
              firstName: (cust.name || "").trim().split(/\s+/)[0] || undefined,
              coins: reward.coins,
              availableBalance: Math.floor(Number(row.available_paise || 0) / POINT_VALUE_PAISE),
              orderShortId: String(orderRef),
              customerId: cust.id,
            });
          }
        }
      } else if (before?.status === "approved") {
        await supabaseAdmin.rpc("loyalty_reverse_review_bonus" as never, { _review_id: data.id } as never);
      }
    } catch (_) {}

    return { ok: true, reward };

  });

export const adminListReviewRequests = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertReviewsAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("review_requests")
      .select("id, order_id, phone, status, provider, sent_at, reminder_sent_at, reviewed_at, last_error, created_at, orders(name)")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return { requests: data ?? [] };
  });

// ─────────── Admin: public brand reply on a review ───────────

export const adminReplyToReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; reply: string }) =>
    z.object({ id: z.string().uuid(), reply: z.string().trim().max(1200) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertReviewsAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const reply = data.reply.trim();
    const patch = reply
      ? { admin_reply: reply, admin_reply_at: new Date().toISOString(), admin_reply_by: context.userId }
      : { admin_reply: null, admin_reply_at: null, admin_reply_by: null };
    const { error } = await supabaseAdmin.from("reviews").update(patch as never).eq("id", data.id);
    if (error) throw new Error(error.message);
    const { logAdminAction } = await import("@/lib/audit.server");
    await logAdminAction({
      context,
      action: reply ? "review_reply_published" : "review_reply_removed",
      entityType: "review",
      entityId: data.id,
      after: { admin_reply: reply || null },
    });
    return { ok: true as const };
  });

